/**
 * Infrastructure — FEAT-MONITOR-001 (Infrastructure monitor tab)
 * Fleet server health (per box: CPU/RAM/disk/load/uptime/PM2), datastore health (Redis/MySQL/
 * ClickHouse/MongoDB), and BullMQ queue depths. Server metrics come from the standalone
 * xnotify-metrics-agent on each box; the rest RRS probes directly.
 */
import React, { useState } from "react";
import {
  Box, Flex, HStack, VStack, Text, Heading, Badge, Button, Select, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, Progress, SimpleGrid,
} from "@chakra-ui/react";
import useInfraPolling from "./useInfraPolling";
import {
  TONE, classifyCpu, classifyRam, classifyDisk, classifyLag, classifyStale,
  fmtInt, fmtRelative, fmtDuration,
} from "./helpers";

const INTERVALS = [{ label: "3s", ms: 3000 }, { label: "5s", ms: 5000 }, { label: "10s", ms: 10000 }, { label: "30s", ms: 30000 }, { label: "Off", ms: 0 }];

export default function Infrastructure() {
  const [intervalMs, setIntervalMs] = useState(5000);
  const [now, setNow] = useState(Date.now());
  const { servers, datastores, queues, error, stale, lastOkAt, busy, refresh } = useInfraPolling({ intervalMs });

  React.useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const staleSec = lastOkAt ? Math.max(0, Math.round((now - new Date(lastOkAt).getTime()) / 1000)) : null;

  const boxes = servers?.boxes || [];
  const upBoxes = boxes.filter((b) => b.ok).length;

  return (
    <Box opacity={stale ? 0.7 : 1} transition="opacity .2s">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={5}>
        <HStack spacing={3} align="center">
          <Heading fontSize="22px" color="#111827">Infrastructure</Heading>
          <HealthPill health={staleSec == null ? "muted" : classifyStale(staleSec)} label={`${upBoxes}/${boxes.length} boxes`} />
          {stale && <Badge colorScheme="orange" variant="subtle">Stale</Badge>}
          <Text fontSize="xs" color="gray.500">{staleSec != null ? `updated ${fmtRelative(lastOkAt)}` : "polling…"}{busy && <Spinner size="xs" ml={2} />}</Text>
        </HStack>
        <HStack spacing={2}>
          <Select size="sm" w="90px" value={intervalMs} onChange={(e) => setIntervalMs(Number(e.target.value))}>
            {INTERVALS.map((i) => <option key={i.label} value={i.ms}>{i.label}</option>)}
          </Select>
          <Button size="sm" onClick={refresh} isLoading={busy}>Refresh</Button>
        </HStack>
      </Flex>

      {error && !servers && !datastores && (
        <Box p={3} mb={4} bg={TONE.crit.bg} color={TONE.crit.fg} borderRadius="md" fontSize="sm">{error}</Box>
      )}

      {/* Fleet servers */}
      <Text fontWeight="700" fontSize="sm" color="#111827" mb={2}>Fleet servers</Text>
      {!boxes.length ? (
        <Box p={4} mb={5} bg="white" borderRadius="lg" borderWidth="1px" fontSize="sm" color="gray.500">
          No metrics agents configured. Deploy <b>xnotify-metrics-agent</b> per box and set <code>MONITOR_AGENTS</code> in RRS.
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3} mb={6}>
          {boxes.map((b) => <ServerCard key={b.name} box={b} />)}
        </SimpleGrid>
      )}

      {/* Datastores + queues */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Datastores stores={datastores?.stores || []} summary={datastores?.summary} loading={!datastores} />
        <Queues queues={queues?.queues || []} loading={!queues} />
      </SimpleGrid>
    </Box>
  );
}

function HealthPill({ health, label }) {
  const t = TONE[health] || TONE.muted;
  return (
    <HStack spacing={1.5} px={2} py={0.5} borderRadius="full" bg={t.bg}>
      <Box w="8px" h="8px" borderRadius="full" bg={t.dot} />
      <Text fontSize="xs" fontWeight="700" color={t.fg}>{label}</Text>
    </HStack>
  );
}

function Meter({ label, pct, tone, sub }) {
  const t = TONE[tone] || TONE.muted;
  return (
    <Box>
      <Flex justify="space-between" fontSize="11px" color="gray.500" mb={0.5}>
        <Text>{label}</Text><Text color={t.fg} fontWeight="600">{pct == null ? "—" : `${pct}%`}</Text>
      </Flex>
      <Progress value={pct || 0} size="sm" borderRadius="full" colorScheme={t.scheme} />
      {sub && <Text fontSize="10px" color="gray.400" mt={0.5}>{sub}</Text>}
    </Box>
  );
}

function ServerCard({ box }) {
  if (!box.ok) {
    return (
      <Box p={4} bg="white" borderRadius="lg" borderWidth="1px" borderColor={TONE.crit.bg}>
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontWeight="700" fontSize="sm">{box.name}</Text>
          <Badge colorScheme="red" variant="subtle">unreachable</Badge>
        </Flex>
        <Text fontSize="xs" color="gray.400">{box.url}</Text>
        <Text fontSize="xs" color={TONE.crit.fg} mt={1}>{box.error}</Text>
      </Box>
    );
  }
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Flex justify="space-between" align="center" mb={2}>
        <Box>
          <Text fontWeight="700" fontSize="sm">{box.node_name || box.name}</Text>
          <Text fontSize="10px" color="gray.400">{box.cores} cores · up {fmtDuration(box.uptime_s)} · {box.platform}</Text>
        </Box>
        <Badge colorScheme="green" variant="subtle">up</Badge>
      </Flex>
      <VStack align="stretch" spacing={2}>
        <Meter label="CPU" pct={box.cpu_pct} tone={classifyCpu(box.cpu_pct)} sub={`load ${(box.load || []).join(" / ")}`} />
        <Meter label="RAM" pct={box.ram_pct} tone={classifyRam(box.ram_pct)} sub={`${fmtInt(box.ram_used_mb)} / ${fmtInt(box.ram_total_mb)} MB`} />
        <Meter label="Disk" pct={box.disk_pct} tone={classifyDisk(box.disk_pct)} sub={box.disk_total_gb ? `${box.disk_used_gb} / ${box.disk_total_gb} GB` : null} />
      </VStack>
      <Flex justify="space-between" mt={2} fontSize="10px" color="gray.400">
        <Text>loop-lag <Text as="span" color={TONE[classifyLag(box.agent_loop_lag_ms)].fg} fontWeight="600">{box.agent_loop_lag_ms}ms</Text></Text>
        <Text>{box.process_count} PM2 proc</Text>
      </Flex>
      {!!(box.processes && box.processes.length) && (
        <Box mt={2} pt={2} borderTopWidth="1px">
          {box.processes.slice(0, 6).map((p) => (
            <Flex key={p.name} justify="space-between" fontSize="10px" color="gray.500">
              <HStack spacing={1}><Box w="6px" h="6px" borderRadius="full" bg={p.status === "online" ? TONE.ok.dot : TONE.crit.dot} /><Text>{p.name}</Text></HStack>
              <Text>{p.cpu_pct}% · {p.mem_mb}MB{p.restarts ? ` · ↻${p.restarts}` : ""}</Text>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Datastores({ stores, summary, loading }) {
  const tone = (s) => (s.status === "up" ? "ok" : s.status === "unknown" ? "muted" : s.status === "degraded" ? "warn" : "crit");
  const line = (s) => {
    if (s.status === "unknown") return s.error || "driver not installed";
    if (s.status !== "up") return s.error || s.status;
    if (s.type === "redis") return `${fmtInt(s.used_memory_mb)}MB${s.used_pct != null ? ` (${s.used_pct}%)` : ""} · ${s.maxmemory_policy || "?"} · ${fmtInt(s.connected_clients)} clients · ${fmtInt(s.ops_per_sec)} ops/s · ${fmtInt(s.evicted_keys)} evicted`;
    if (s.type === "mysql") return `${s.version} · ${fmtInt(s.threads_connected)} conns (${fmtInt(s.threads_running)} running) · ${fmtInt(s.slow_queries)} slow`;
    if (s.type === "clickhouse") return `${fmtInt(s.active_parts)} parts · ${s.disk_bytes_gb}GB · ${fmtInt(s.running_merges)} merges`;
    if (s.type === "mongodb") return `${s.version || ""} · ${fmtInt(s.connections_current)} conns · ${fmtInt(s.resident_mb)}MB res${s.replica_set ? ` · rs:${s.replica_set.set}` : ""}`;
    return "";
  };
  const summaryText = summary && Object.keys(summary).length
    ? Object.entries(summary).map(([t, v]) => `${v.up}/${v.total} ${t}`).join(" · ") : null;
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Flex justify="space-between" align="baseline" mb={2}>
        <Text fontWeight="700" fontSize="sm" color="#111827">Datastores (fleet)</Text>
        {summaryText && <Text fontSize="10px" color="gray.400">{summaryText}</Text>}
      </Flex>
      {loading ? <Flex h="80px" align="center" justify="center"><Spinner size="sm" /></Flex> :
        !stores.length ? <Text fontSize="xs" color="gray.400">No datastores reported. Declare <code>AGENT_PROBES</code> on each box's agent.</Text> : (
          <VStack align="stretch" spacing={3}>
            {stores.map((s, i) => {
              const t = TONE[tone(s)];
              return (
                <Box key={`${s.box}-${s.type}-${s.name}-${i}`}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Box w="8px" h="8px" borderRadius="full" bg={t.dot} />
                      <Text fontSize="sm" fontWeight="600">{s.type}/{s.name}</Text>
                      <Badge colorScheme="gray" variant="subtle" fontSize="9px">{s.box}</Badge>
                    </HStack>
                    <HStack spacing={2}>
                      {s.latency_ms != null && <Text fontSize="10px" color="gray.400">{s.latency_ms}ms</Text>}
                      <Badge colorScheme={t.scheme} variant="subtle" fontSize="10px">{s.status}</Badge>
                    </HStack>
                  </Flex>
                  <Text fontSize="11px" color="gray.500" mt={0.5}>{line(s)}</Text>
                </Box>
              );
            })}
          </VStack>
        )}
    </Box>
  );
}

function Queues({ queues, loading }) {
  const depthTone = (d) => (d === 0 ? "muted" : d < 100 ? "ok" : d < 1000 ? "warn" : "crit");
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Text fontWeight="700" fontSize="sm" color="#111827" mb={2}>Queues (BullMQ, fleet)</Text>
      {loading ? <Flex h="80px" align="center" justify="center"><Spinner size="sm" /></Flex> :
        !queues.length ? <Text fontSize="xs" color="gray.400">No BullMQ queues discovered on any Redis instance.</Text> : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead><Tr><Th>Queue</Th><Th>Instance</Th><Th isNumeric>Depth</Th><Th isNumeric>Delayed</Th><Th isNumeric>Failed</Th></Tr></Thead>
              <Tbody>
                {queues.map((q, i) => (
                  <Tr key={`${q.box}-${q.redis}-${q.queue}-${i}`}>
                    <Td><Text fontFamily="mono" fontSize="xs">{q.queue}</Text></Td>
                    <Td><HStack spacing={1}><Text fontSize="xs" color="gray.600">{q.redis}</Text><Badge colorScheme="gray" variant="subtle" fontSize="9px">{q.box}</Badge></HStack></Td>
                    <Td isNumeric><Text color={TONE[depthTone(q.depth)].fg} fontWeight="600">{fmtInt(q.depth)}</Text></Td>
                    <Td isNumeric>{fmtInt(q.delayed)}</Td>
                    <Td isNumeric><Text color={q.failed > 0 ? TONE.crit.fg : "gray.400"}>{fmtInt(q.failed)}</Text></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
    </Box>
  );
}
